<template>
  <div class="athletes-summary-bar" :class="{ compact }">
    <div
      class="summary-pill goal"
      :class="{ ready: approvedCount >= minAthletes, clickable: approvedCount > 0, active: activeFilter === 'approved' }"
      @click="handleClick('approved', approvedCount)"
    >
      <i class="fa-solid fa-circle-check"></i>
      {{ approvedCount }}
    </div>
    <div
      class="summary-pill declined"
      :class="{ clickable: declinedCount > 0, active: activeFilter === 'declined' }"
      @click="handleClick('declined', declinedCount)"
    >
      <i class="fa-solid fa-user-slash"></i>
      {{ declinedCount }}
    </div>
    <div
      class="summary-pill pending"
      :class="{ clickable: pendingCount > 0, active: activeFilter === 'pending' }"
      @click="handleClick('pending', pendingCount)"
    >
      <i class="fa-solid fa-hourglass-half"></i>
      {{ pendingCount }}
    </div>
    <div
      class="summary-pill invited"
      :class="{ clickable: invitedCount > 0, active: activeFilter === 'invited' }"
      @click="handleClick('invited', invitedCount)"
    >
      <i class="fa-solid fa-envelope"></i>
      {{ invitedCount }}
    </div>
    <div
      class="summary-pill goal"
      :class="{ ready: paiedCount >= minAthletes, clickable: paiedCount > 0, active: activeFilter === 'paid' }"
      @click="handleClick('paid', paiedCount)"
    >
      <i class="fa-solid fa-money-check-alt"></i>
      {{ paiedCount }}
    </div>

    <div
      class="summary-pill goal"
      :class="{ ready: approvedCount >= minAthletes, clickable: true, active: activeFilter === null }"
      @click="handleClick('reset', 1)"
    >
      <i class="fa-solid fa-flag-checkered"></i>
      {{ approvedCount }} / {{ minAthletes }}
    </div>
  </div>
</template>

<script>

export default {
  name: "AthleteSummaryBar",
  emits: ['filter'],
  props: {
    competition: {
      type: Object,
      required: true
    },
    compact: {
      type: Boolean,
      default: false
    },
    activeFilter: {
      type: String,
      default: null
    }
  },
  computed: {
    summary() {
      return this.competition.athletes_summary || {};
    },
    approvedCount() {
      return this.summary.approved || 0;
    },
    minAthletes() {
      const raw = this.competition?.athletes_min ?? this.summary?.min_approved;
      const val = Number(raw);
      return Number.isFinite(val) && val > 0 ? val : 0;
    },
    declinedCount() {
      return this.summary.declined || 0;
    },
    pendingCount() {
      return this.summary.pending || 0;
    },
    invitedCount() {
      return this.summary.invited || 0;
    },
    paiedCount() {
      return this.summary.paied || 0;
    },
    activeTotalCount() {
      return this.summary.active_total || 0;
    },
  },
  methods: {
    handleClick(type, count) {
      // Solo filtrar si hay elementos; el último pill siempre resetea
      if (type !== 'reset' && (!count || count <= 0)) return;
      this.$emit('filter', type === 'reset' ? null : type);
    }
  }
};

</script>

<style scoped>
.athletes-summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: nowrap;
}

.summary-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  background-color: #f0f0f0;
  color: #333;
  transition: all 0.3s ease;
  justify-content: center;
}

.athletes-summary-bar:not(.compact) .summary-pill {
  flex: 1 0 calc((100% - 60px) / 6); /* 6 pills per row, accounting for 5 gaps of 12px */
  min-width: 0;
}

.summary-pill i {
  font-size: 16px;
}

.summary-pill.clickable {
  cursor: pointer;
}

.summary-pill.active {
  box-shadow: 0 0 0 2px #4c56af;
}

/* Compact mode */
.athletes-summary-bar.compact {
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  max-width: 240px; /* ajusta este valor según el tamaño del pill */
  margin: 10px auto;
}

.athletes-summary-bar.compact .summary-pill {
  flex: 0 0 calc(33.333% - 8px); /* 3 por fila, menos el gap */
  display: flex;
  justify-content: center;
  text-align: center;
}

.athletes-summary-bar.compact .summary-pill i {
  font-size: 12px;
}

/* Colores por estado (se mantienen) */
.summary-pill.approved,
.summary-pill.declined,
.summary-pill.pending,
.summary-pill.paied,
.summary-pill.invited {
  background-color: #c5c5c5;
  color: #5b5b5b;
}

.summary-pill.goal {
  background-color: #e2e3e5;
  color: #383d41;
}

.summary-pill.goal.ready {
  background-color: #c3e6cb;
  color: #155724;
}
@media (max-width: 768px) {
  .athletes-summary-bar {
    gap: 8px;
    flex-wrap: wrap;
  }
  .summary-pill {
    gap: 6px;
    padding: 4px 8px;
    font-size: 14px;
  }
  .athletes-summary-bar:not(.compact) .summary-pill {
    flex: 1 0 calc((100% - 16px) / 3); /* 3 per row on mobile = 2 filas para 6 */
  }
  .athletes-summary-bar.compact {
    flex-wrap: wrap;
    gap: 6px;
    max-width: 100%;
    margin: 10px auto;
  }

  .athletes-summary-bar.compact .summary-pill {
    flex: 0 0 calc(33.333% - 8px); /* 3 por fila, menos el gap */
    justify-content: center;
    text-align: center;
    padding: 6px 8px;
  }
}

</style>
